import React, { useMemo, useRef, memo, useCallback, useState } from "react";
import ChevronIcon from "@/common/images/icons/chevron-left.svg?react";
import { clsx } from "clsx";
import { Tooltip } from "components/tooltip/Tooltip";
import { Button } from "components/buttons/Button";
import { useNicheTooltip } from "./hooks/useNicheTooltip";
import { INicheFeedbackRequest, INicheFeedbackResponse } from "@/common/services/types";
import { useMediaQuery } from "usehooks-ts";
import { TABLET_WIDTH } from "@/common/utils/device";
import "./Vote.scss";
import { PulseCircle } from "components/UI/PulseCircle";
import { useVisitorInfo } from "@/common/hooks";
import { useDismissNicheTooltip } from "./hooks/useDismissNicheTooltip";

export type TAddFeedback = (params: Omit<INicheFeedbackRequest, "niche">) => void;

interface IVote {
  gif: string;
  addNicheFeedback: TAddFeedback;
  position: number;
  gifFeedback: INicheFeedbackResponse;
  isVisible: boolean;
}

const CLICK_TIMER = 2000;

export const Vote: React.FC<IVote> = memo(({
  addNicheFeedback,
  gif,
  position,
  gifFeedback,
  isVisible,
}) => {
  const isDesktop = useMediaQuery(`(min-width: ${TABLET_WIDTH}px)`);
  const [showTooltip, setShowTooltip] = useState(false);
  const qtyRef = useRef<HTMLDivElement>(null);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  const currentUser = useVisitorInfo();
  const { isDismissNicheTooltip, onDismissNicheTooltip } = useDismissNicheTooltip(currentUser);

  const isNicheNoticeTooltip = useNicheTooltip({
    position,
    isDismissNicheTooltip,
    isVisible
  });

  const { upvotes, downvotes, state } = gifFeedback;
  const qty = useMemo(() => upvotes - downvotes, [upvotes, downvotes]);

  const handleVote = useCallback((voteType: "up" | "down") => {
    if (!isDisabled) {
      setIsDisabled(true);
      addNicheFeedback({ gif, state: voteType });
      setTimeout(() => setIsDisabled(false), CLICK_TIMER);
    }
  }, [addNicheFeedback, gif, isDisabled]);

  const onUpVote = useCallback(() => handleVote("up"), [handleVote]);
  const onDownVote = useCallback(() => handleVote("down"), [handleVote]);

  const toggleTooltip = useCallback((show: boolean) => setShowTooltip(show), []);

  const voteTooltipText = useMemo(() => {
    const parts = [];
    if (upvotes > 0) {
      parts.push(`${upvotes} Upvote${upvotes > 1 ? "s" : ""}`);
    }

    if (upvotes > 0 && downvotes > 0) {
      parts.push("·");
    }

    if (downvotes > 0) {
      parts.push(`${downvotes} Downvote${downvotes > 1 ? "s" : ""}`);
    }

    return parts.join(" ");
  }, [upvotes, downvotes]);

  // calculate styles
  const upVoteClasses = clsx("Vote-Btn", "Vote-Btn_isUp", state === "up" && "Vote-Btn_isUpActive");
  const downVoteClasses = clsx("Vote-Btn", "Vote-Btn_isDown", state === "down" && "Vote-Btn_isDownActive");
  const qtyClasses = clsx("Vote-Qty", state && "Vote-Qty_isVoted", (qty > 0) && "Vote-Qty_isPositive", qty < 0 && "Vote-Qty_isNegative");

  return (
    <div className="Vote">
      <div className={clsx("Vote-Control", isNicheNoticeTooltip && "Vote-Control_isNotice")} ref={qtyRef}>
        <button className={upVoteClasses} onClick={onUpVote} aria-label="Upvote" disabled={isDisabled}>
          <ChevronIcon />
        </button>
        <span
          className={qtyClasses}
          onMouseEnter={() => toggleTooltip(true)}
          onMouseLeave={() => toggleTooltip(false)}
        >{qty}</span>
        <button className={downVoteClasses} onClick={onDownVote} aria-label="Downvote" disabled={isDisabled}>
          <ChevronIcon />
        </button>
        {isNicheNoticeTooltip && <PulseCircle />}
      </div>
      {upvotes === 0 && downvotes === 0 ? null : (
        <Tooltip isArrow={false} position={isDesktop ? "topCenter" : "topRight"} anchorEl={qtyRef.current} active={showTooltip}>
          <span>{voteTooltipText}</span>
        </Tooltip>
      )}
      {isNicheNoticeTooltip && <Tooltip
        size="xl"
        position={isDesktop ? "topRight" : "bottomRight"}
        anchorEl={qtyRef.current}
        active={isNicheNoticeTooltip}
        className="NoticeTooltip"
        onClose={() => onDismissNicheTooltip(true)}
      >
        <span>Use the arrows to vote on this content's relevance to this Niche.</span>
        <Button variant="primary" size="xs" onClick={() => onDismissNicheTooltip(false)}>Got it</Button>
      </Tooltip>}
    </div>
  );
});
